import { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

export default function InstallAppBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Don't show if dismissed recently (7 days)
    const dismissed = localStorage.getItem('pcc_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show after short delay (no native prompt)
    if (isiOS) {
      const timer = setTimeout(() => setShow(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pcc_install_dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 sm:bottom-4 left-4 right-4 z-50 max-w-md mx-auto animate-in slide-in-from-bottom" data-testid="install-app-banner">
      <div className="bg-navy rounded-xl shadow-2xl border border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 p-4">
          <img src="/pwa-icon-192.png" alt="PCC" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-sm">Get the Chiefs App</p>
            <p className="text-white/60 text-xs">Add to your home screen for quick access</p>
          </div>
          <button onClick={handleDismiss} className="text-white/40 hover:text-white p-1 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isIOS ? (
          <div className="px-4 pb-4">
            <div className="bg-white/10 rounded-lg p-3 flex items-center gap-3">
              <Share className="w-5 h-5 text-gold flex-shrink-0" />
              <p className="text-white/80 text-xs">
                Tap <strong className="text-gold">Share</strong> then <strong className="text-gold">"Add to Home Screen"</strong>
              </p>
            </div>
          </div>
        ) : deferredPrompt ? (
          <div className="px-4 pb-4">
            <button
              onClick={handleInstall}
              className="w-full bg-red hover:bg-red/90 text-white font-bold text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
              data-testid="install-app-button"
            >
              <Download className="w-4 h-4" />
              Install App
            </button>
          </div>
        ) : (
          <div className="px-4 pb-4">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-white/80 text-xs text-center">
                Use your browser menu to "Add to Home Screen"
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
